import Container from 'components/atomic/atoms/container';
import CalendarIcon from 'components/icon/calendar-icon';
import { AutoGrid } from 'components/atomic/atoms/auto-grid';
import { validateVariant } from 'ui/utils/validate-variant';
import type { OpenHouseData } from './use-open-house';

export const OPEN_HOUSE_CARD_VARIANTS = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
} as const;

export type OpenHouseCardVariant = typeof OPEN_HOUSE_CARD_VARIANTS[keyof typeof OPEN_HOUSE_CARD_VARIANTS];

const VARIANT_CONFIG: Record<OpenHouseCardVariant, { margin: string }> = {
  [OPEN_HOUSE_CARD_VARIANTS.MOBILE]: {
    margin: '1em',
  },
  [OPEN_HOUSE_CARD_VARIANTS.DESKTOP]: {
    margin: '0',
  },
};

export interface OpenHouseCardProps {
  data: OpenHouseData;
  variant: OpenHouseCardVariant;
  className?: string;
}

export function OpenHouseCard({ data, variant, className }: OpenHouseCardProps) {
  const { openHouses, label } = data;

  if (!validateVariant('OpenHouseCard', variant, OPEN_HOUSE_CARD_VARIANTS)) {
    return null;
  }

  if (openHouses.length === 0) {
    return null;
  }

  const config = VARIANT_CONFIG[variant];

  return (
    <Container
      className={className}
      data-testid="ui-open-house-card"
      sx={{
        padding: '1em',
        borderRadius: 'var(--themes-buttons-border-radius-small)',
        margin: config.margin,
        backgroundColor: '#EFEFEF',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        flexWrap: 'wrap',
        justifyContent: { xs: 'flex-start', sm: 'space-between' },
        alignItems: 'center',
      }}
    >
      <Container
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5em',
          marginBottom: { xs: '1em', sm: 0 },
          fontWeight: 'bold',
          '& svg': {
            width: '1em',
            height: '1em',
            marginRight: '0.5em',
          },
        }}
      >
        <CalendarIcon />
        {label}
      </Container>

      <AutoGrid columns={{ xs: 1, sm: 2 }} rowGap="0.25em" columnGap="1.5em">
        {openHouses.map((openHouse, index) => (
          <Container
            key={index}
            component="span"
            sx={{
              lineHeight: '1.5em',
              whiteSpace: 'nowrap',
              opacity: openHouse.isActive ? 1 : 0.6,
            }}
          >
            {openHouse.label}
          </Container>
        ))}
      </AutoGrid>
    </Container>
  );
}
